import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ShellErrorBoundary, ThemeProvider } from '@vigeo/ui';
import { fixtureMode } from './lib/data-mode.js';
import { retryRead } from './lib/query-retry.js';
import { routes } from './routes.js';

if (fixtureMode) {
  // Development only. `fixtureMode` is the literal `false` in a production build, so this
  // branch is dead code and the fixture dataset, adapter and QA bar are never emitted.
  await import('../dev-fixtures/adapter.js');
}

const queryClient = new QueryClient({
  // Thirty seconds fresh: a second mount of the same query in one render cycle (the shell's
  // auth read and a page's) reuses the answer instead of asking again, and cached per-person
  // readiness is reused during the session (Gate 8). Mutations invalidate what they change.
  // One retry for a fault, none for a 404 (#724): an unknown person id says so at once.
  defaultOptions: { queries: { retry: retryRead, refetchOnWindowFocus: false, staleTime: 30_000 } },
});

const router = createBrowserRouter(routes);

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ShellErrorBoundary>
          <RouterProvider router={router} />
        </ShellErrorBoundary>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
